import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
  getBytes
} from 'firebase/storage';
import {
  getFunctions,
  httpsCallable
} from 'firebase/functions';

// ============================================================================
// FIREBASE CONFIGURATION
// ============================================================================

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize secondary app for user creation (prevents logout of admin session)
const secondaryApp = initializeApp(firebaseConfig, 'secondary');

// Initialize services
export const auth = getAuth(app);
export const authSecondary = getAuth(secondaryApp);
export const db = getFirestore(app);
export const storage = getStorage(app);
// Specifica region europea per le Cloud Functions
export const functions = getFunctions(app, 'europe-west2');

// ============================================================================
// TYPES FOR GEMINI RESPONSE
// ============================================================================

export interface GeminiWineRecognition {
  name: string;
  producer: string;
  vintage?: number;
  type: 'red' | 'white' | 'rose' | 'sparkling' | 'dessert' | 'fortified';
  grape?: string;
  denomination?: string;
  region?: string;
  alcoholContent?: number;
  estimatedValue?: number;
  pairings?: string[];
  tastingNotes?: string;
  servingTemperature?: string;
  glassType?: string;
  // Conservazione e invecchiamento
  storageTemperature?: string;
  storageNotes?: string;
  drinkByYear?: number;
  agingPotential?: string;
  confidence?: number;
}

// ============================================================================
// GEMINI AI WINE RECOGNITION (via Cloud Function)
// ============================================================================

const identifyWineWithGeminiFn = httpsCallable<
  { imageData?: string[]; imageTypes?: string[]; imageUrls?: string[]; existingData?: Partial<GeminiWineRecognition> },
  GeminiWineRecognition[]
>(functions, 'identifyWineWithGemini');

export const identifyWineFromStorageUrls = async (
  imageUrls: string[],
  existingData?: Partial<GeminiWineRecognition>
): Promise<GeminiWineRecognition[]> => {
  const result = await identifyWineWithGeminiFn({ imageUrls, existingData });
  if (!result.data || result.data.length === 0) {
    throw new Error('Nessun vino riconosciuto nell\'immagine.');
  }
  return result.data;
};

export const identifyWineFromImage = async (
  imageFiles: File | File[],
  existingData?: Partial<GeminiWineRecognition>
): Promise<GeminiWineRecognition[]> => {
  const files = Array.isArray(imageFiles) ? imageFiles : [imageFiles];

  // Se non ci sono immagini, chiedi arricchimento testo alla Cloud Function
  if (files.length === 0 || (files.length === 1 && files[0].size === 0)) {
    if (!existingData?.name || !existingData?.producer) {
      throw new Error('Immagine o dati esistenti richiesti');
    }
    const result = await identifyWineWithGeminiFn({ existingData });
    return result.data;
  }

  let base64Images: string[];
  try {
    base64Images = await Promise.all(files.map(file => fileToBase64(file)));
  } catch {
    throw new Error('Errore nella lettura dell\'immagine. Il file potrebbe essere corrotto.');
  }

  const imageTypes = files.map(file => file.type);

  try {
    const result = await identifyWineWithGeminiFn({
      imageData: base64Images,
      imageTypes,
      existingData,
    });

    if (!result.data || result.data.length === 0) {
      throw new Error('Nessun vino riconosciuto nell\'immagine.');
    }

    return result.data;
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error('Errore durante il riconoscimento del vino. Riprova o inserisci manualmente.');
  }
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

// ============================================================================
// DATE UTILITIES
// ============================================================================

/**
 * Formatta un Timestamp Firebase in stringa leggibile
 * Formato: dd/mm/yyyy HH:mm
 */
export const formatDate = (timestamp: Timestamp | Date | null | undefined): string => {
  if (!timestamp) return 'N/A';

  const date = timestamp instanceof Timestamp ? timestamp.toDate() : timestamp;

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Formatta data in formato corto (es. "5 gen 2024")
 */
export const formatShortDate = (timestamp: Timestamp | Date | null | undefined): string => {
  if (!timestamp) return 'N/A';

  const date = timestamp instanceof Timestamp ? timestamp.toDate() : timestamp;

  return new Intl.DateTimeFormat('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

// ============================================================================
// FIRESTORE HELPERS
// ============================================================================

/**
 * Ottiene un documento da Firestore
 */
export const getDocument = async <T>(collectionName: string, docId: string): Promise<T | null> => {
  try {
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as T;
    }
    return null;
  } catch (error) {
    console.error(`Error getting document from ${collectionName}:`, error);
    throw error;
  }
};

/**
 * Salva un documento in Firestore
 */
export const saveDocument = async (
  collectionName: string,
  docId: string,
  data: any
): Promise<void> => {
  try {
    const docRef = doc(db, collectionName, docId);
    await setDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.error(`Error saving document to ${collectionName}:`, error);
    throw error;
  }
};

/**
 * Aggiorna un documento in Firestore
 */
export const updateDocument = async (
  collectionName: string,
  docId: string,
  data: any
): Promise<void> => {
  try {
    const docRef = doc(db, collectionName, docId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error(`Error updating document in ${collectionName}:`, error);
    throw error;
  }
};

/**
 * Elimina un documento da Firestore
 */
export const deleteDocument = async (collectionName: string, docId: string): Promise<void> => {
  try {
    const docRef = doc(db, collectionName, docId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error(`Error deleting document from ${collectionName}:`, error);
    throw error;
  }
};

// ============================================================================
// STORAGE HELPERS
// ============================================================================

/**
 * Upload di un file su Firebase Storage
 */
export const uploadFile = async (
  path: string,
  file: File
): Promise<string> => {
  try {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Elimina un file da Firebase Storage
 */
export const deleteFile = async (path: string): Promise<void> => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

// ============================================================================
// CLOUD FUNCTIONS HELPERS
// ============================================================================

export const callFunction = <T, R>(functionName: string) => {
  const callable = httpsCallable<T, R>(functions, functionName);
  return callable;
};

// ============================================================================
// OPENFOODFACTS API - BARCODE LOOKUP
// ============================================================================

export interface OpenFoodFactsProduct {
  found: boolean;
  name?: string;
  producer?: string;
  alcoholContent?: number;
  region?: string;
  imageUrl?: string;
}

/**
 * Cerca un prodotto (vino) tramite barcode usando OpenFoodFacts API
 */
export const searchProductByBarcode = async (barcode: string): Promise<OpenFoodFactsProduct> => {
  try {
    const response = await fetch(
      `https://world.openfoodfacts.org/api/v2/product/${barcode}.json`
    );

    if (!response.ok) {
      return { found: false };
    }

    const data = await response.json();

    if (data.status !== 1 || !data.product) {
      return { found: false };
    }

    const product = data.product;

    // Estrai informazioni rilevanti
    return {
      found: true,
      name: product.product_name || product.product_name_it || product.product_name_en,
      producer: product.brands,
      // OpenFoodFacts salva l'alcol come percentuale * 100 nel campo alcohol_100g
      alcoholContent: product.alcohol_100g ? parseFloat(product.alcohol_100g) : undefined,
      region: product.origins || product.manufacturing_places,
      imageUrl: product.image_url || product.image_front_url,
    };
  } catch (error) {
    console.error('Error fetching product from OpenFoodFacts:', error);
    return { found: false };
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  firebaseSignOut as signOut,
  onAuthStateChanged,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
  getBytes,
};

export type { FirebaseUser };
